import { useId } from 'react';

import { usePlaybackProgress } from '@/hooks/usePlaybackProgress';

interface Props {
  /** The player id of the sound this play controls (`search-12`, `discover-3`…). */
  audioId: string;
  /** `circle` around a round play button, `rounded` around a playable cover. */
  shape: 'circle' | 'rounded';
  /** Side of the button or cover it surrounds, in px. */
  size: number;
  /** Corner radius of the cover, in px (`rounded` only). */
  radius?: number;
}

/** Gap between the target and the indicator, on each side. */
const OFFSET = 4;
const STROKE = 2.5;
/** Inset of the rounded outline inside its box, so the stroke is not clipped. */
const INSET = 1.5;

/**
 * A rounded square traced from the middle of its top edge, clockwise — the same
 * "from noon" start as the ring, so both shapes fill the same way.
 */
function roundedOutline(box: number, radius: number): string {
  const lo = INSET;
  const hi = box - INSET;
  const r = radius + OFFSET - INSET;
  const mid = box / 2;
  return [
    `M ${mid} ${lo}`,
    `H ${hi - r}`,
    `A ${r} ${r} 0 0 1 ${hi} ${lo + r}`,
    `V ${hi - r}`,
    `A ${r} ${r} 0 0 1 ${hi - r} ${hi}`,
    `H ${lo + r}`,
    `A ${r} ${r} 0 0 1 ${lo} ${hi - r}`,
    `V ${lo + r}`,
    `A ${r} ${r} 0 0 1 ${lo + r} ${lo}`,
    'Z',
  ].join(' ');
}

/**
 * playback-progress-ring — how far the current preview has played, as a yellow
 * gradient stroke around its play (maquette C, `web/docs/DESIGN-SYSTEM.md`).
 * Renders nothing unless `audioId` is the current, unfinished sound. Decorative:
 * the play button keeps its own label and every tap.
 */
export default function PlaybackProgress({ audioId, shape, size, radius = 0 }: Props) {
  const progress = usePlaybackProgress(audioId);
  // useId() may hold `:` or `«»`, which some WebKit builds reject inside `url(#…)`.
  const gradientId = `playback-gradient-${useId().replace(/[^\w-]/g, '')}`;
  if (progress === null) return null;

  const box = size + OFFSET * 2;
  const centre = box / 2;
  const percent = Math.round(progress * 100);
  const stroke = {
    fill: 'none',
    strokeWidth: STROKE,
    pathLength: 100,
  } as const;
  const outline =
    shape === 'circle'
      ? { r: size / 2 + OFFSET / 2, cx: centre, cy: centre }
      : { d: roundedOutline(box, radius) };
  const Shape = shape === 'circle' ? 'circle' : 'path';

  return (
    <svg
      width={box}
      height={box}
      viewBox={`0 0 ${box} ${box}`}
      aria-hidden="true"
      data-testid={`playback-progress-${audioId}`}
      data-progress={percent}
      // -1 = the 4 px OFFSET
      className="tw:pointer-events-none tw:absolute tw:-left-1 tw:-top-1"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--tw-color-yellow-1)" />
          <stop offset="50%" stopColor="var(--tw-color-yellow-primary)" />
          <stop offset="100%" stopColor="var(--tw-color-yellow-4)" />
        </linearGradient>
      </defs>
      <Shape data-part="rail" {...outline} {...stroke} className="tw:stroke-white/18" />
      {/* At 0 % only the rail: a zero-length dash with a round cap shows as a dot. */}
      {percent > 0 && (
        <Shape
          data-part="arc"
          {...outline}
          {...stroke}
          stroke={`url(#${gradientId})`}
          strokeLinecap="round"
          strokeDasharray={100}
          strokeDashoffset={100 - percent}
          transform={shape === 'circle' ? `rotate(-90 ${centre} ${centre})` : undefined}
          className="tw:transition-[stroke-dashoffset] tw:duration-250 tw:ease-linear tw:motion-reduce:transition-none"
        />
      )}
    </svg>
  );
}
